import React from "react";
import {
  Box,
  ButtonBase,
  Chip,
  Grid,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";

import { designTokens } from "../theme";

const colors = {
  page: designTokens.surface.page,
  paper: designTokens.surface.paper,
  subtle: designTokens.surface.subtle,
  border: designTokens.border.default,
  borderStrong: designTokens.overlay.brandMedium,
  text: designTokens.text.primary,
  secondary: designTokens.text.secondary,
  muted: designTokens.text.muted,
  brand: designTokens.brand.dark,
  brandSoft: designTokens.overlay.brandSoft,
  info: designTokens.state.info,
  warning: designTokens.state.warning,
};

type HomeMenu = {
  title: string;
  shortTitle: string;
  description: string;
  icon: React.ElementType;
  path: string;
  children: string[];
  accent: string;
  softBackground: string;
  number: string;
};

const homeMenus: HomeMenu[] = [
  {
    number: "01",
    title: "営業ダッシュボード",
    shortTitle: "状況を確認",
    description:
      "営業全体の状況を俯瞰し、見込み顧客・既存顧客ごとの進捗や傾向を確認します。",
    icon: AssessmentRoundedIcon,
    path: "/dashboard",
    children: ["全体", "見込み顧客", "既存顧客"],
    accent: colors.brand,
    softBackground: colors.brandSoft,
  },
  {
    number: "02",
    title: "顧客一覧",
    shortTitle: "顧客を探す",
    description:
      "顧客情報を一元的に確認し、顧客区分ごとの検索・閲覧・管理を行います。",
    icon: GroupsRoundedIcon,
    path: "/customers",
    children: ["全体顧客", "見込み顧客", "既存顧客", "取引停止"],
    accent: colors.info,
    softBackground: "rgba(50,134,184,0.09)",
  },
  {
    number: "03",
    title: "日報",
    shortTitle: "活動を記録",
    description:
      "営業活動の内容を登録し、過去の日報や顧客への対応履歴を確認します。",
    icon: DescriptionRoundedIcon,
    path: "/sales-reports",
    children: ["日報一覧", "日報登録"],
    accent: colors.warning,
    softBackground: "rgba(217,142,4,0.09)",
  },
  {
    number: "04",
    title: "案件管理",
    shortTitle: "案件を進める",
    description:
      "営業案件の進捗、期限、担当者、予定金額を確認し、案件単位で管理します。",
    icon: WorkRoundedIcon,
    path: "/projects",
    children: ["全案件", "見込み顧客", "既存顧客"],
    accent: "#6B5FC7",
    softBackground: "rgba(107,95,199,0.09)",
  },
];

export default function DashHome() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        color: colors.text,
        background: `
          radial-gradient(circle at 92% 8%, rgba(152,188,46,0.09), transparent 26%),
          radial-gradient(circle at 7% 92%, rgba(50,134,184,0.05), transparent 24%),
          ${colors.page}
        `,
      }}
    >
      {/* 固定ヘッダーの高さを確保 */}
      <Toolbar />

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1480,
            mx: "auto",
          }}
        >
          {/* ページヘッダー */}
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(115deg, rgba(255,255,255,0.98), rgba(247,250,242,0.98) 62%, rgba(235,243,225,0.98))",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.35,
                pointerEvents: "none",
                backgroundImage:
                  "radial-gradient(rgba(93,116,62,0.14) 0.7px, transparent 0.7px)",
                backgroundSize: "15px 15px",
                maskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.68), transparent 62%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.68), transparent 62%)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{
                position: "relative",
                zIndex: 1,
                p: { xs: 1.7, md: 2.2 },
              }}
            >
              <Stack direction="row" spacing={1.3} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 2.2,
                    color: colors.brand,
                    backgroundColor: colors.brandSoft,
                    border: `1px solid ${colors.borderStrong}`,
                  }}
                >
                  <DashboardCustomizeRoundedIcon sx={{ fontSize: 27 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: colors.brand,
                      fontSize: 11.5,
                      fontWeight: 900,
                      letterSpacing: "0.13em",
                      lineHeight: 1,
                    }}
                  >
                    SALES MANAGEMENT
                  </Typography>

                  <Typography
                    component="h1"
                    sx={{
                      mt: 0.7,
                      color: colors.text,
                      fontSize: { xs: 21, md: 25 },
                      fontWeight: 900,
                      lineHeight: 1.25,
                    }}
                  >
                    営業管理ホーム
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.45,
                      color: colors.secondary,
                      fontSize: 13,
                      lineHeight: 1.7,
                    }}
                  >
                    行いたい業務を選択してください。
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1.4,
                  py: 0.9,
                  borderRadius: 2,
                  color: colors.secondary,
                  backgroundColor: "rgba(255,255,255,0.72)",
                  border: `1px solid ${colors.border}`,
                }}
              >
                <TouchAppRoundedIcon
                  sx={{ fontSize: 19, color: colors.brand }}
                />
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 700,
                  }}
                >
                  カードを選択して各業務画面へ移動
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* メニューカード */}
          <Grid container spacing={1.5}>
            {homeMenus.map((menu) => {
              const Icon = menu.icon;

              return (
                <Grid
                  size={{ xs: 12, md: 6 }}
                  key={menu.title}
                >
                  <ButtonBase
                    onClick={() => navigate(menu.path)}
                    aria-label={`${menu.title}を開く`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: { xs: 225, md: 245 },
                      display: "block",
                      textAlign: "left",
                      borderRadius: 3,
                      overflow: "hidden",
                      position: "relative",
                      backgroundColor: colors.paper,
                      border: `1px solid ${colors.border}`,
                      boxShadow: "0 6px 20px rgba(15,23,42,0.05)",
                      transition:
                        "transform .18s ease, border-color .18s ease, box-shadow .18s ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(135deg, ${menu.softBackground}, transparent 56%)`,
                        opacity: 0.92,
                        pointerEvents: "none",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: 118,
                        height: 118,
                        borderRadius: "0 0 0 100%",
                        backgroundColor: menu.softBackground,
                        opacity: 0.7,
                        pointerEvents: "none",
                      },
                      "&:hover": {
                        transform: "translateY(-3px)",
                        borderColor: menu.accent,
                        boxShadow: "0 14px 34px rgba(15,23,42,0.09)",
                      },
                      "&:hover .menu-arrow": {
                        transform: "translateX(3px)",
                        backgroundColor: menu.accent,
                        color: "#fff",
                      },
                      "&:focus-visible": {
                        outline: `3px solid ${menu.softBackground}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Stack
                      sx={{
                        height: "100%",
                        minHeight: { xs: 225, md: 245 },
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          height: 5,
                          backgroundColor: menu.accent,
                        }}
                      />

                      <Stack
                        spacing={1.7}
                        sx={{
                          flex: 1,
                          p: { xs: 2, sm: 2.3 },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1.5}
                        >
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                                borderRadius: 2.2,
                                color: menu.accent,
                                backgroundColor: menu.softBackground,
                                border: `1px solid ${colors.border}`,
                              }}
                            >
                              <Icon sx={{ fontSize: 28 }} />
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  color: menu.accent,
                                  fontSize: 11.5,
                                  fontWeight: 900,
                                  letterSpacing: "0.08em",
                                }}
                              >
                                {menu.number} / {menu.shortTitle}
                              </Typography>

                              <Typography
                                component="h2"
                                sx={{
                                  mt: 0.2,
                                  color: colors.text,
                                  fontSize: { xs: 18, sm: 19 },
                                  fontWeight: 900,
                                  lineHeight: 1.35,
                                }}
                              >
                                {menu.title}
                              </Typography>
                            </Box>
                          </Stack>

                          <Box
                            className="menu-arrow"
                            sx={{
                              width: 36,
                              height: 36,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                              borderRadius: "50%",
                              color: menu.accent,
                              backgroundColor: colors.subtle,
                              border: `1px solid ${colors.border}`,
                              transition:
                                "transform .18s ease, background-color .18s ease, color .18s ease",
                            }}
                          >
                            <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Stack>

                        <Typography
                          sx={{
                            color: colors.secondary,
                            fontSize: 13,
                            lineHeight: 1.75,
                            minHeight: { md: 46 },
                          }}
                        >
                          {menu.description}
                        </Typography>

                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          gap={0.75}
                          sx={{
                            mt: "auto",
                            pt: 0.4,
                          }}
                        >
                          {menu.children.map((child) => (
                            <Chip
                              key={child}
                              size="small"
                              label={child}
                              sx={{
                                height: 27,
                                color: menu.accent,
                                backgroundColor: "rgba(255,255,255,0.82)",
                                border: `1px solid ${colors.border}`,
                                fontSize: 11.5,
                                fontWeight: 800,
                                "& .MuiChip-label": {
                                  px: 1.1,
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </Stack>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}